import React from 'react';
import { Link } from 'react-static';
import { IArtists } from 'types/IArtists';
import {Event, IArtist} from 'types/IArtist';
import moment from 'moment';
import styled from 'styled-components';
import em from 'polished/lib/helpers/em';
import Moment from 'react-moment';
import { getRelativeUrl } from '@flow/utils/cleaners';
import { getVenueNameByID, getVenues, isNew } from '@flow/utils/helperUtils';
import { Badge } from '@flow/components/ui/';
import FlowStripe from '@flow/components/ticker/FlowStripe';
import {
  GridWrapper,
  GridItem,
  GridItemContent,
  GridItemContentTitle,
  GridRowAfterStripe,
  GridBadgeWrapper,
  GridImageWrapper,
  LazyGridImage
} from '@flow/components/grid/grid';

const TextVersionVenueTitle = styled.span`
  display: inline-block;
  font-size: 2.1em;
  line-height: ${em(3.2, 2.1)};
  font-family: Flow-Poster-Bold;
  color: ${props => props.theme.highlightColor};
  text-transform: uppercase;
  margin: 0 ${em(2, 2.1)} 0 0;
`;

const TextVersionArtists = styled.span`
  > span {
    &:last-child {
      margin-right: ${em(2, 2.1)};
      &:after {
        display: none;
      }
    }
    
    @media (max-width: 600px) {
      margin-right: 0 !important;
      &:after {
        display: none;
      }
    }
  }
`;

const TextVersionArtistTitle = styled.span`
  display: inline-block;
  font-size: 2.1em;
  line-height: ${em(3.2, 2.1)};
  font-family: Flow-Poster-Bold;
  text-transform: uppercase;
  //margin-bottom: ${em(1.5, 2.1)};
  
  a {
    display: inline-block;
    transition: color 0.2s ease-out;
    &:${props => props.theme.hoverProperty} {
      color: ${props => props.theme.highlightColor};
    }
  }
  
  &:after {
    content: '/';
    display: inline-block;
    margin: 0 ${em(1, 2.1)};
  }
`;

const DayTitle = styled.span`
  display: block;
  font-size: 10em;
  font-family: Flow-Inverted;
  text-transform: uppercase;
  white-space: nowrap;
  margin-right: ${em(4, 10)};
`;

const DayTitleWrapper = styled.div`
  display: flex;
  padding: 0 3em;
  margin-bottom: 5em;
  
  @media (max-width: 1920px) {
    ${DayTitle} {
      font-size: ${100 / (1920 * 0.01)}vw;
    }
    margin-bottom: ${50 / (1920 * 0.01)}vw;
  }
  
  @media (max-width: 1500px) {
    flex-wrap: wrap;
    
    .day-artist-text-version {
      margin-top: 4em;
    }
    
    ${DayTitle} {
      width: 100%;
    }
  }
  
  @media (max-width: 960px) {
    ${DayTitle} {
      font-size: 5em;  
    }
    margin-bottom: 2.5em;
  }
  
  @media (max-width: 720px) {
    .day-artist-text-version {
      font-size: 90%;
    }
  }
  
  @media (max-width: 600px) {
    text-align: center;
    .day-artist-text-version {
      display: none;
    }
    
    ${TextVersionVenueTitle}, ${TextVersionArtistTitle} {
      display: block;
      font-size: 1.8em;
    }
    
    ${TextVersionVenueTitle} {
      margin: ${em(1, 1.8)} 0;
    }
    
    ${TextVersionArtistTitle} a {
      padding: ${em(0.5, 1.8)} 0;
    }
  }
  
  @media (max-width: 800px) {
    .day-artist-text-version {
      //display: none;
    }
  }
`;

const ArtistVenue = styled.span`
  display: block;
  font-size: 2.1em;
  font-family: Flow-Poster-Bold;
  margin-bottom: ${em(2, 2.1)};
`;

const ArtistContent = styled.div`
  time, .event-time {
    display: inline-block;
    font-size: 2.1em;
    line-height: ${em(3.2, 2.1)};
    font-family: Flow-Poster-Bold;
  }
  
  .event-time {
    margin-left: ${em(0.7, 2.1)};
  }
`;

const ArtistEventInfo = styled.div`
  margin-bottom: 2em;
`;

interface Day {
  date: string;
  artists: IArtist[];
}

interface Venue {
  name: string;
  artists: IArtist[];
}

const renderDayTitle = (locale: string, date: string, format: string, doNotShowAdditionalText:boolean = false) => {
  if (moment(date).isValid()) {
    return (
      <Moment locale={locale} format={format} date={date}/>
    )
  } else if(!doNotShowAdditionalText) {
    return (
      <span>Tba</span>
    )
  }
  return null;
};

const renderEventTime = (event: Event): React.ReactNode => {
  if (moment(event.date).isValid() && event.time) {
    return (
      <span className='event-time'> - {event.time}</span>
    )
  }
  return null;
};

const moveItemInArrayFromIndexToIndex = (array: Array<any>, fromIndex: number, toIndex: number) => {
  if (fromIndex === toIndex) return array;

  const newArray = [...array];

  const target = newArray[fromIndex];
  const inc = toIndex < fromIndex ? -1 : 1;

  for (let i = fromIndex; i !== toIndex; i += inc) {
    newArray[i] = newArray[i + inc];
  }

  newArray[toIndex] = target;

  return newArray;
};

const generateUniqueVenuesFromArtists = (artists: IArtists): Venue[] => {
  if (artists) {
    let venues: Venue[] = [];
    let venueIDs: number[] = getVenues() || [];
    for (let i: number = 0; i < venueIDs.length; i++) {
      let venueID: number = venueIDs[i];
      let venueArtists: IArtist[] = artists.filter((artist: IArtist) => {
        return artist.event.venue_id === venueID;
      });
      if (venueArtists.length > 0) {
        const venueName: string = getVenueNameByID(venueID);
        venues.push(
          {
            name: venueName,
            artists: venueArtists
          }
        );
      }
    }
    return venues;
  }
  return [];
};

const renderArtistInTextFormat = (venues: Venue[]): React.ReactNode => {
  // move TBA venue artist on top
  if (venues && venues.length > 1) {
    const tbaVenueIndex: number = venues.findIndex(venue => venue.name === 'TBA');
    if (tbaVenueIndex !== -1 && tbaVenueIndex !== 0) {
      venues = moveItemInArrayFromIndexToIndex(venues, tbaVenueIndex, 0);
    }
  }

  let showVenueTitles: boolean = true; //venues.length > 1; // Päätetty 24.6.2022 palaverissa, että näytetään venue otsikko aina

  return (
    <div>
      {venues.map((venue: Venue, index: number) => (
        <React.Fragment key={index}>
          {showVenueTitles && Boolean(venue.name !== 'TBA') &&
          <TextVersionVenueTitle>{venue.name}</TextVersionVenueTitle>
          }
          <TextVersionArtists>
            {venue.artists.map((artist: IArtist, index: number) => (
              <TextVersionArtistTitle key={index}>
                <Link to={getRelativeUrl(artist.link)}>{artist.title}</Link>
              </TextVersionArtistTitle>
            ))}
          </TextVersionArtists>
        </React.Fragment>
      ))}
    </div>
  )
};

export const renderDays = (artists: IArtists, appLanguage: IAppLanguage, isMobile: boolean) => {
  let days: Day[] = [];
  if (artists) {
    artists.map((artist: IArtist) => {
      let day: Day = days.find((day: Day) => day.date === artist.event.date);
      if (!day) {
        days.push({date: artist.event.date, artists: [artist]});
      } else {
        day.artists.push(artist);
      }
    });

    let tba = days.find((day: Day) => day.date === 'tba');
    days = days.filter(( day: Day ) => {
      return moment(day.date).isValid();
    });
    days.sort((a, b) => {
      return Number(a.date ? a.date.split('-').join('') : 0) - Number(b.date ? b.date.split('-').join('') : 0);
    });
    if (tba) {
      days.push(tba);
    }
  }

  const doNotRenderTitle: boolean = !days || (days.length === 1 && days[0].date === 'tba');

  return (
    <div>
      { days && days.map((day: Day, index: number) => {
        const availableVenues: Venue[] = generateUniqueVenuesFromArtists(day.artists || []);
        return (
          <div key={index}>
            {!doNotRenderTitle &&
            <DayTitleWrapper>
              { !(days.length === 1 && days[0].date === 'tba') &&
              <DayTitle>
                {renderDayTitle(appLanguage.code, day.date, 'ddd D.M.')}
              </DayTitle>
              }
              {availableVenues.length > 0 &&
                <div className='day-artist-text-version'>
                  {renderArtistInTextFormat(availableVenues)}
                </div>
              }
            </DayTitleWrapper>
            }
            {renderList(day.artists, appLanguage, isMobile)}
          </div>
        )
      })}
    </div>
  )
};

export const renderList = (artists: IArtists, appLanguage: IAppLanguage, isMobile: boolean) => {
  let artistsList: IArtists = artists;

  if (artistsList) {
    return (
      <GridWrapper>
        {artistsList.map((artist: IArtist, index: number) => (
          renderItem(artists.length-1, artist, index, appLanguage.code, isMobile)
        ))}
      </GridWrapper>
    );
  } else {
    return null;
  }
};

const renderItem = (artistsCount: number, artist: IArtist, index: number, locale: string, isMobile: boolean) => {
  return [
    renderArtist(artist, index, locale, isMobile),
    (((index+1) % 4 == 0) || index === artistsCount) &&
    <GridRowAfterStripe key={2} className={index === artistsCount ? 'force-show' : ''}>
      <FlowStripe animate={true} color={'#000000'} />
    </GridRowAfterStripe>
  ]
};

const renderArtist = (artist: IArtist, index: number, locale: string, isMobile: boolean) => {
  if (artist) {
    const venue: string = getVenueNameByID(artist.event.venue_id);

    return (
      <GridItem key={index}>
        <Link to={ getRelativeUrl(artist.link) }>
          {isNew(artist.date) &&
            <GridBadgeWrapper>
              <Badge type={'new'} />
            </GridBadgeWrapper>
          }
          {(Boolean (artist.images) && Boolean (artist.images.landscape) && Boolean(artist.images.landscape[0])) &&
          <GridImageWrapper allowGrayscaleEffect={false}>
            <LazyGridImage image={artist.images.landscape[0]} />
          </GridImageWrapper>
          }
          <ArtistContent>
            <GridItemContent>
              <GridItemContentTitle>{ artist.title }</GridItemContentTitle>
              <ArtistEventInfo>
                {venue && venue !== 'TBA' &&
                <ArtistVenue>{venue}</ArtistVenue>
                }
                {renderDayTitle(locale, artist.event.date, 'ddd D.M.', true)}
                {renderEventTime(artist.event)}
              </ArtistEventInfo>
            </GridItemContent>
          </ArtistContent>
        </Link>
      </GridItem>
    )
  } else {
    return null;
  }
};



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/scenes/artists/ui/index.tsx